import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Lightbox } from "@/components/Lightbox";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/service-hero.jpg";
import commercialImg from "@/assets/commercial-v2.jpg";
import grindingImg from "@/assets/grinding-v2.jpg";
import metallicBasementImg from "@/assets/metallic-v3.jpg";
import flakeTanImg from "@/assets/project-flake-tan.jpg.asset.json";
import flakeGlossyImg from "@/assets/project-flake-glossy.jpg.asset.json";
import flakeBlueImg from "@/assets/project-flake-blue.jpg.asset.json";
import garageGreyImg from "@/assets/project-garage-grey.jpg.asset.json";
import broadcastImg from "@/assets/project-broadcast.png.asset.json";
import flakeRollerImg from "@/assets/img_6313.jpg.asset.json";
import garageStorageImg from "@/assets/img_6422.jpg.asset.json";
import kitchenFlakeImg from "@/assets/img_6379.jpg.asset.json";
import garageStairsImg from "@/assets/img_6508.jpg.asset.json";

import flakeTan800 from "@/assets/project-flake-tan-800.webp.asset.json";
import flakeTan1600 from "@/assets/project-flake-tan-1600.webp.asset.json";
import commercial800 from "@/assets/commercial-800.webp.asset.json";
import commercial1600 from "@/assets/commercial-1600.webp.asset.json";
import flakeGlossy800 from "@/assets/flake-glossy-800.webp.asset.json";
import flakeGlossy1600 from "@/assets/flake-glossy-1600.webp.asset.json";
import garageGrey800 from "@/assets/garage-grey-800.webp.asset.json";
import garageGrey1600 from "@/assets/garage-grey-1600.webp.asset.json";
import grinding800 from "@/assets/grinding-800.webp.asset.json";
import grinding1600 from "@/assets/grinding-1600.webp.asset.json";
import flakeBlue800 from "@/assets/flake-blue-800.webp.asset.json";
import flakeBlue1600 from "@/assets/flake-blue-1600.webp.asset.json";
import broadcast800 from "@/assets/broadcast-800.webp.asset.json";
import broadcast1600 from "@/assets/broadcast-1600.webp.asset.json";
import metallicBasement800 from "@/assets/metallic-basement-800.webp.asset.json";
import metallicBasement1600 from "@/assets/metallic-basement-1600.webp.asset.json";
import flakeRoller800 from "@/assets/flake-roller-800.webp.asset.json";
import flakeRoller1600 from "@/assets/flake-roller-1600.webp.asset.json";
import garageStorage800 from "@/assets/garage-storage-800.webp.asset.json";
import garageStorage1600 from "@/assets/garage-storage-1600.webp.asset.json";
import kitchenFlake800 from "@/assets/kitchen-flake-800.webp.asset.json";
import kitchenFlake1600 from "@/assets/kitchen-flake-1600.webp.asset.json";
import garageStairs800 from "@/assets/garage-stairs-800.webp.asset.json";
import garageStairs1600 from "@/assets/garage-stairs-1600.webp.asset.json";

export const Route = createFileRoute("/gallery")({
  validateSearch: (search: Record<string, unknown>) => ({
    category: typeof search.category === "string" ? search.category : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Project Gallery | MLC Epoxy Floors Calgary Region" },
      { name: "description", content: "Before & after photos of garage floor coating projects throughout Calgary, Calgary and surrounding areas." },
      { property: "og:title", content: "Gallery | MLC Epoxy Floors" },
      { property: "og:description", content: "Browse premium epoxy, metallic, and polyaspartic floor coating projects from across the Calgary region." },
      { property: "og:url", content: "/gallery" },
    ],
    links: [{ rel: "canonical", href: "/gallery" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([{ name: "Home", href: "/" }, { name: "Gallery", href: "/gallery" }])) }],
  }),
  component: GalleryPage,
});

const CATEGORIES = ["All", "Garage Floors", "EPOXY FLAKE SYSTEM", "Residential", "Commercial", "PATIO DECK"] as const;

const ITEMS = [
  { cat: "Garage Floors", fallback: flakeTanImg.url, webp800: flakeTan800.url, webp1600: flakeTan1600.url, title: "Flake Epoxy Garage", location: "Calgary", desc: "Tan flake blend" },
  { cat: "Commercial", fallback: commercialImg, webp800: commercial800.url, webp1600: commercial1600.url, title: "Commercial Showroom", location: "Calgary", desc: "Solid color commercial epoxy" },
  { cat: "Garage Floors", fallback: flakeGlossyImg.url, webp800: flakeGlossy800.url, webp1600: flakeGlossy1600.url, title: "Glossy Flake Garage", location: "Calgary", desc: "High-gloss polyaspartic topcoat" },
  { cat: "Garage Floors", fallback: garageGreyImg.url, webp800: garageGrey800.url, webp1600: garageGrey1600.url, title: "Triple-Car Garage", location: "Calgary", desc: "Grey flake blend" },
  { cat: "PATIO DECK", fallback: grindingImg, webp800: grinding800.url, webp1600: grinding1600.url, title: "Patio Deck Flake System", location: "Airdrie", desc: "Polished concrete with sealer" },
  { cat: "Garage Floors", fallback: flakeBlueImg.url, webp800: flakeBlue800.url, webp1600: flakeBlue1600.url, title: "Orbit Flake Garage", location: "Calgary", desc: "Custom orbit flake blend" },
  { cat: "Residential", fallback: broadcastImg.url, webp800: broadcast800.url, webp1600: broadcast1600.url, title: "Flake Broadcast Install", location: "Calgary", desc: "Full broadcast saturation" },
  { cat: "EPOXY FLAKE SYSTEM", fallback: metallicBasementImg, webp800: metallicBasement800.url, webp1600: metallicBasement1600.url, title: "Commercial Office Floor", location: "Silver Spring", desc: "Flake system epoxy finish" },
  { cat: "Garage Floors", fallback: flakeRollerImg.url, webp800: flakeRoller800.url, webp1600: flakeRoller1600.url, title: "Polyaspartic Topcoat In Progress", location: "Calgary", desc: "Back-rolling polyaspartic topcoat" },
  { cat: "Residential", fallback: garageStorageImg.url, webp800: garageStorage800.url, webp1600: garageStorage1600.url, title: "Tan Flake Garage Install", location: "Calgary", desc: "Neutral flake blend with storage shelving" },
  { cat: "Commercial", fallback: kitchenFlakeImg.url, webp800: kitchenFlake800.url, webp1600: kitchenFlake1600.url, title: "Commercial Kitchen Flake Floor", location: "Calgary", desc: "Slip-resistant flake system" },
  { cat: "Garage Floors", fallback: garageStairsImg.url, webp800: garageStairs800.url, webp1600: garageStairs1600.url, title: "Garage with Coved Base", location: "Calgary", desc: "Full flake broadcast with coved walls" },
];

const GRID_SIZES = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 33vw";

function GalleryPage() {
  const { category } = Route.useSearch();
  const initial = (CATEGORIES as readonly string[]).includes(category ?? "") ? (category as (typeof CATEGORIES)[number]) : "All";
  const [filter, setFilter] = useState<(typeof CATEGORIES)[number]>(initial);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  useEffect(() => { setFilter(initial); }, [initial]);
  const visible = filter === "All" ? ITEMS : ITEMS.filter((i) => i.cat === filter);
  const lightboxImages = visible.map((v) => ({ src: v.fallback, alt: `${v.title} — ${v.location}`, caption: `${v.title} — ${v.location}` }));
  return (
    <>
      <PageHero eyebrow="Portfolio" title="Project Gallery" body="A sample of recent garage floor and concrete coating projects across the Calgary region. Every floor is diamond-ground, professionally installed, and topcoated in polyaspartic." image={heroImg} />
      <section className="container-x py-16">
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { setFilter(c); setLightboxIdx(null); }}
              className={`rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${filter === c ? "border-copper bg-copper text-white" : "border-border bg-charcoal text-silver hover:border-copper hover:text-copper"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid gap-1.5 grid-cols-3 sm:gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((it, i) => (
            <figure key={i} className="group overflow-hidden border border-border bg-charcoal">
              <button
                type="button"
                onClick={() => setLightboxIdx(i)}
                aria-label={`Open ${it.title} in lightbox`}
                className="block aspect-[4/3] w-full overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-copper"
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${it.webp800} 800w, ${it.webp1600} 1600w`}
                    sizes={GRID_SIZES}
                  />
                  <img
                    src={it.fallback}
                    alt={it.title}
                    width={1600}
                    height={1200}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </picture>
              </button>
              <figcaption className="p-3 sm:p-5">
                <p className="text-[10px] sm:text-xs uppercase tracking-widest text-copper">{it.cat}</p>
                <p className="mt-1 sm:mt-2 text-xs sm:text-base text-silver-light font-semibold leading-snug">{it.title}</p>
                <p className="hidden sm:block text-sm text-silver">{it.desc}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
      <CTASection />
      {lightboxIdx !== null && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndexChange={setLightboxIdx}
        />
      )}
    </>
  );
}
